import React from 'react';
import { CertQuestionCodeDropdown } from '@unity-learning/shared';
import { ContextCode } from './ContextCode';

interface QuestionCodeDropdownProps {
    question: Omit<CertQuestionCodeDropdown, 'validation'>;
    value: Record<string, string>;
    onChange: (value: Record<string, string>) => void;
    disabled?: boolean;
    /** Revue (page de résultats) : bonne réponse liste par liste. Chaque liste prend la couleur juste ou fausse. */
    reviewAgainst?: Record<string, string>;
}

export const QuestionCodeDropdown: React.FC<QuestionCodeDropdownProps> = ({
    question,
    value,
    onChange,
    disabled = false,
    reviewAgainst
}) => {
    const { content } = question;
    const { context_code, reference_code, code_template, dropdowns } = content;
    const isReview = reviewAgainst !== undefined;

    const handleDropdownChange = (dropdownId: string, selectedValue: string) => {
        if (disabled) return;
        onChange({
            ...value,
            [dropdownId]: selectedValue
        });
    };

    // Parse the code template and replace [[dropdown_id]] with select elements
    const renderCodeWithDropdowns = () => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;

        // Match all [[dropdown_id]] patterns
        const regex = /\[\[(\w+)\]\]/g;
        let match;

        while ((match = regex.exec(code_template)) !== null) {
            // Add the text before this match
            if (match.index > lastIndex) {
                parts.push(
                    <span key={`text-${lastIndex}`} className="whitespace-pre-wrap">
                        {code_template.slice(lastIndex, match.index)}
                    </span>
                );
            }

            const dropdownId = match[1];
            const dropdownConfig = dropdowns[dropdownId];

            if (dropdownConfig) {
                // Même règle que la notation serveur : espaces et casse ignorés
                const chosen = value[dropdownId] || '';
                const isRight = isReview
                    && chosen.trim().toLowerCase() === (reviewAgainst[dropdownId] ?? '').trim().toLowerCase();
                const colourClass = isReview
                    ? (isRight
                        ? 'bg-green-50 dark:bg-green-950 border-green-500 dark:border-green-400 text-green-800 dark:text-green-200'
                        : 'bg-red-50 dark:bg-red-950 border-red-500 dark:border-red-400 text-red-700 dark:text-red-200')
                    : `bg-white dark:bg-gray-800
              border-gray-300 dark:border-gray-600
              text-gray-800 dark:text-gray-200
              focus:border-blue-500 focus:ring-1 focus:ring-blue-500`;
                parts.push(
                    <select
                        key={`dropdown-${dropdownId}`}
                        value={chosen}
                        onChange={(e) => handleDropdownChange(dropdownId, e.target.value)}
                        disabled={disabled}
                        title={isReview ? (isRight ? 'Réponse juste' : 'Réponse fausse') : undefined}
                        className={`
              mx-1 my-0.5 pl-2 pr-8 py-1 rounded border text-sm font-mono max-w-full
              ${colourClass}
              ${disabled ? (isReview ? 'cursor-default' : 'cursor-not-allowed opacity-60') : 'cursor-pointer'}
            `}
                    >
                        <option value="">{dropdownConfig.placeholder}</option>
                        {dropdownConfig.options.map((opt, idx) => (
                            <option key={idx} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>
                );
            }

            lastIndex = match.index + match[0].length;
        }

        // Add any remaining text
        if (lastIndex < code_template.length) {
            parts.push(
                <span key={`text-final`} className="whitespace-pre-wrap">
                    {code_template.slice(lastIndex)}
                </span>
            );
        }

        return parts;
    };

    return (
        <div className="w-full space-y-4">
            {/* Context Code (if present) */}
            {context_code && <ContextCode code={context_code} />}

            {/* Reference Code (API hint) */}
            {reference_code && (
                <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Référence API :
                    </div>
                    <code className="block whitespace-pre-wrap text-sm font-mono text-blue-700 dark:text-blue-400">
                        {reference_code}
                    </code>
                </div>
            )}

            {/* Code Editor with Inline Dropdowns */}
            <div className="bg-gray-900 rounded-lg overflow-hidden">
                <pre className="p-4 text-sm font-mono text-gray-100 whitespace-pre-wrap leading-relaxed">
                    {renderCodeWithDropdowns()}
                </pre>
            </div>
        </div>
    );
};

export default QuestionCodeDropdown;
